// HeaderChatUI.jsx
import React, { useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";

import MediaModal from "./modals/MediaModal";
import SearchModal from "./modals/SearchModal";
import ChatParticipantsModal from "./ChatParticipantsModal";
import { setActiveChatIdLocal } from "../../actions/chatActions";

const getId = (value) => String(value?._id || value || "");

const getUserName = (user) => {
    if (!user) return "Собеседник";

    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

    return name || user.email || user.username || "Собеседник";
};

const getTypingUserName = (user) => {
    if (!user) return "";

    return [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
        user.email ||
        user.username ||
        "";
};

const MAX_CHAT_BACKGROUND_BYTES = 1.5 * 1024 * 1024;

const CHAT_BACKGROUND_OPTIONS = [
    { key: "default", label: "Обычный" },
    { key: "soft", label: "Мягкий" },
    { key: "dots", label: "Точки" },
    { key: "grid", label: "Сетка" },
];

const getTypingText = (users = []) => {
    const names = (users || [])
        .map(getTypingUserName)
        .filter(Boolean);

    if (!names.length) return "";

    const visible = names.slice(0, 2);
    const extra = names.length > 2 ? ` +${names.length - 2}` : "";
    const verb = names.length === 1 ? "печатает" : "печатают";

    return `${visible.join(", ")}${extra} ${verb}…`;
};

export default function HeaderChatUI({
    chat,
    userId,
    officeId,
    office,
    variant = "default",
    titleOverride = null,
    typingUsers = [],
    presenceText = null,
    onOpenFloating = null,
    backgroundPreset = "default",
    onBackgroundChange = null,
}) {
    const dispatch = useDispatch();

    const [menuOpen, setMenuOpen] = useState(false);
    const [participantsOpen, setParticipantsOpen] = useState(false);
    const [mediaModal, setMediaModal] = useState({ open: false, type: "image" });
    const [searchOpen, setSearchOpen] = useState(false);
    const [localChat, setLocalChat] = useState(chat);
    const [backgroundError, setBackgroundError] = useState("");
    const backgroundInputRef = useRef(null);

    const actualChat = localChat?._id === chat?._id ? localChat : chat;
    const floating = variant === "floating";

    const computedTitle = useMemo(() => {
        if (!actualChat) return "Чат";
        if (actualChat.chatType === "saved") return actualChat.title || "Сохранённое";
        if (actualChat.chatType === "scene_guest") return actualChat.title || "Чат по 3D-сцене";
        if (actualChat.entity?.type && actualChat.entity.type !== "none") return actualChat.entity.title || actualChat.title || "Обсуждение";
        if (actualChat.isGroup || actualChat.chatType === "group") return actualChat.title || "Общий чат";
        if (actualChat.user) return getUserName(actualChat.user);

        const participants = Array.isArray(actualChat.participants) ? actualChat.participants : [];
        const other = participants.find((item) => getId(item) !== getId(userId));

        return getUserName(other);
    }, [actualChat, userId]);

    const title = titleOverride || computedTitle;

    const subtitle = useMemo(() => {
        if (!actualChat) return "";
        if (actualChat.chatType === "saved") return "Личные заметки и файлы";
        if (actualChat.chatType === "scene_guest") {
            return actualChat.guestAccess?.customerName || "Заказчик без регистрации";
        }
        if (actualChat.entity?.type === "product") return actualChat.entity.subtitle || "Обсуждение товара";
        if (actualChat.entity?.type === "order") return actualChat.entity.subtitle || "Обсуждение заказа";
        if (actualChat.chatType === "group" || actualChat.isGroup) return `${actualChat.participants?.length || 0} участников`;

        return office?.name || "Личный диалог";
    }, [actualChat, office]);

    const typingText = useMemo(
        () => getTypingText(typingUsers),
        [typingUsers]
    );
    const statusText = typingText || presenceText || subtitle;

    const canShowParticipants = !!(
        (actualChat?.isGroup || actualChat?.chatType === "group") &&
        !["saved", "scene_guest"].includes(actualChat?.chatType)
    );

    const openMedia = (type) => {
        setMediaModal({ open: true, type });
        setMenuOpen(false);
    };

    const openParticipants = () => {
        setParticipantsOpen(true);
        setMenuOpen(false);
    };

    const openSearch = () => {
        setSearchOpen(true);
        setMenuOpen(false);
    };

    const changeBackground = (preset) => {
        setBackgroundError("");
        const applied = onBackgroundChange?.({ preset, image: null });

        if (applied === false) {
            setBackgroundError("Не удалось сохранить фон в браузере.");
            return;
        }

        setMenuOpen(false);
    };

    const handleBackgroundFile = (event) => {
        const file = event.target.files?.[0] || null;
        event.target.value = "";

        if (!file) return;

        if (!file.type?.startsWith("image/")) {
            setBackgroundError("Выберите изображение.");
            return;
        }

        if (file.size > MAX_CHAT_BACKGROUND_BYTES) {
            setBackgroundError("Фон слишком большой. Максимум 1,5 МБ.");
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            const image = typeof reader.result === "string" ? reader.result : "";
            if (!image) {
                setBackgroundError("Не удалось прочитать изображение.");
                return;
            }

            const applied = onBackgroundChange?.({ preset: "custom", image });

            if (applied === false) {
                setBackgroundError("Не удалось сохранить фон. Попробуйте файл меньше.");
                return;
            }

            setBackgroundError("");
            setMenuOpen(false);
        };

        reader.onerror = () => setBackgroundError("Не удалось прочитать изображение.");
        reader.readAsDataURL(file);
    };

    return (
        <div className={`chat-header-AO ${floating ? "chat-header-AO--floating" : ""}`}>
            <div className="chat-header-AO__main">
                {!floating && (
                    <div className="chat-header-AO__avatar">{title.slice(0, 1).toUpperCase()}</div>
                )}

                <div className="chat-header-AO__identity">
                    {floating ? (
                        <div className="chat-header-AO__floating-line">
                            <div className="chat-header-AO__title">{title}</div>
                            {!!statusText && (
                                <div
                                    className={`chat-header-AO__subtitle ${typingText ? "is-typing" : ""}`}
                                    aria-live="polite"
                                >
                                    {!!typingText && (
                                        <span className="chat-header-AO__typing-dots" aria-hidden="true">
                                            <i /><i /><i />
                                        </span>
                                    )}
                                    <span className="chat-header-AO__status-text">{statusText}</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="chat-header-AO__title">{title}</div>
                            <div
                                className={`chat-header-AO__subtitle ${typingText ? "is-typing" : ""}`}
                                aria-live="polite"
                            >
                                {!!typingText && (
                                    <span className="chat-header-AO__typing-dots" aria-hidden="true">
                                        <i /><i /><i />
                                    </span>
                                )}
                                <span className="chat-header-AO__status-text">{statusText}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div
                className="chat-header-AO__actions"
                onMouseDown={floating ? (event) => event.stopPropagation() : undefined}
                onTouchStart={floating ? (event) => event.stopPropagation() : undefined}
            >
                {!floating && canShowParticipants && (
                    <button
                        type="button"
                        className="chat-header-AO__icon"
                        onClick={openParticipants}
                        title="Участники"
                    >
                        👥
                    </button>
                )}

                {!floating && (
                    <button type="button" className="chat-header-AO__icon" onClick={openSearch} title="Поиск">
                        🔎
                    </button>
                )}

                {!floating && onOpenFloating && (
                    <button
                        type="button"
                        className="chat-header-AO__icon"
                        onClick={onOpenFloating}
                        title="Открыть мини-чат"
                        aria-label="Открыть мини-чат"
                    >
                        <PictureInPictureAltIcon sx={{ fontSize: 19 }} />
                    </button>
                )}

                <div className="chat-header-AO__menu-wrap">
                    <button
                        type="button"
                        className="chat-header-AO__icon"
                        onClick={() => setMenuOpen((state) => !state)}
                        title="Меню чата"
                    >
                        ⋯
                    </button>

                    {menuOpen && (
                        <>
                            <button type="button" className="chat-header-AO__backdrop" onClick={() => setMenuOpen(false)} />
                            <div className="chat-header-AO__menu">
                                {floating && canShowParticipants && (
                                    <button type="button" onClick={openParticipants}>Участники</button>
                                )}
                                {floating && (
                                    <button type="button" onClick={openSearch}>Поиск по чату</button>
                                )}
                                <button type="button" onClick={() => openMedia("image")}>Фотографии</button>
                                <button type="button" onClick={() => openMedia("video")}>Видео</button>
                                <button type="button" onClick={() => openMedia("voice")}>Голосовые</button>
                                <button type="button" onClick={() => openMedia("doc")}>Документы</button>
                                <button type="button" onClick={() => openMedia("link")}>Ссылки</button>

                                {!!onBackgroundChange && (
                                    <>
                                        <div className="chat-header-AO__menu-separator" />
                                        <div className="chat-header-AO__menu-label">Фон чата</div>

                                        <div className="chat-header-AO__background-grid">
                                            {CHAT_BACKGROUND_OPTIONS.map((item) => (
                                                <button
                                                    key={item.key}
                                                    type="button"
                                                    className={`chat-header-AO__background-option is-${item.key} ${backgroundPreset === item.key ? "is-active" : ""}`}
                                                    onClick={() => changeBackground(item.key)}
                                                    title={item.label}
                                                >
                                                    <span />
                                                    <b>{item.label}</b>
                                                </button>
                                            ))}

                                            <button
                                                type="button"
                                                className={`chat-header-AO__background-option is-custom ${backgroundPreset === "custom" ? "is-active" : ""}`}
                                                onClick={() => backgroundInputRef.current?.click()}
                                                title="Своя картинка"
                                            >
                                                <span>＋</span>
                                                <b>Своя</b>
                                            </button>
                                        </div>

                                        {!!backgroundError && (
                                            <div className="chat-header-AO__background-error" role="alert">
                                                {backgroundError}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {!!onBackgroundChange && (
                <input
                    ref={backgroundInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleBackgroundFile}
                />
            )}

            {mediaModal.open && (
                <MediaModal chatId={actualChat?._id} type={mediaModal.type} onClose={() => setMediaModal({ open: false, type: "image" })} />
            )}

            {searchOpen && <SearchModal chatId={actualChat?._id} onClose={() => setSearchOpen(false)} />}

            {participantsOpen && (
                <ChatParticipantsModal
                    chat={actualChat}
                    officeId={officeId}
                    open={participantsOpen}
                    onClose={() => setParticipantsOpen(false)}
                    onUpdated={(nextChat) => {
                        setLocalChat(nextChat);
                        dispatch(setActiveChatIdLocal(nextChat?._id || actualChat?._id));
                    }}
                />
            )}
        </div>
    );
}
